import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { comprimirImagen } from "@/lib/image";
import { ESTADOS_US } from "@/lib/constants";
import { diaEn } from "@/lib/chofer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { ArrowLeft, Camera, Loader2, Fuel } from "lucide-react";
import { toast } from "sonner";
import CamaraDocumento from "@/components/CamaraDocumento";
import { t } from "@/lib/i18n";

export default function ChoferDiesel() {
  const navigate = useNavigate();
  const [scan, setScan] = useState(null); // {loading, result}
  const [recibos, setRecibos] = useState([]);
  const [camaraAbierta, setCamaraAbierta] = useState(false);

  const cargar = useCallback(async () => {
    try { const { data } = await api.get("/chofer/diesel"); setRecibos(data); } catch { /* */ }
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  const onFotoCapturada = async (file) => {
    setCamaraAbierta(false);
    setScan({ loading: true, result: null });
    try {
      const comprimida = await comprimirImagen(file);
      const fd = new FormData();
      fd.append("file", comprimida);
      const { data } = await api.post("/chofer/diesel/escanear", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setScan({ loading: false, result: data });
    } catch (err) {
      toast.error(apiError(err));
      setScan(null);
    }
  };

  return (
    <div className="bg-paper">  {/* Fix Q (2026-10-13): app del chofer en inglés; el alto y el margen inferior los pone ChoferLayout */}
      <header className="bg-paper sticky top-0 z-10 border-b border-line">
        <div className="max-w-md mx-auto flex items-center gap-1 px-2 py-2">
          <button data-testid="diesel-volver" onClick={() => navigate("/chofer")} className="h-12 w-12 flex items-center justify-center text-ink" aria-label={t("Back")}><ArrowLeft className="h-6 w-6" /></button>
          <h1 className="font-display font-bold text-[26px] leading-[1.05] text-ink">{t("Fuel")}</h1>
        </div>
      </header>

      <main className="max-w-md mx-auto p-4 space-y-4">
        <Button data-testid="diesel-cargar-btn" onClick={() => setCamaraAbierta(true)} className="w-full h-[54px] text-base">
          <Camera className="h-5 w-5 mr-2" />{t(" Add fuel receipt")}</Button>

        <div className="space-y-2">
          <p className="eyebrow">{t("My receipts (last 30 days)")}</p>
          {recibos.length === 0 && <p className="text-sm text-ink-3 text-center py-6">{t("You have not added any receipts yet")}</p>}
          {recibos.map((r) => (
            <div key={r.id} data-testid={`diesel-recibo-${r.id}`} className="bg-white rounded-xl border border-line p-3 flex items-center gap-3">
              <span className="h-10 w-10 rounded-md bg-[#ECE8E0] font-mono font-semibold text-[14px] flex items-center justify-center shrink-0">{r.estado_us}</span>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-ink">{diaEn(r.fecha)}</p>
                <p className="text-sm text-ink-3 truncate">{r.estacion || t("No station")}</p>
                {r.corregido_por_chofer && <p className="text-xs text-[#B07A12]">{t("Manually corrected")}</p>}
              </div>
              <div className="text-right">
                <p className="font-mono font-semibold text-ink">${r.monto.toFixed(2)}</p>
                <p className="font-mono text-[12.5px] text-ink-3">{t("{v0} gal", { v0: r.galones.toFixed(3) })}</p>
              </div>
            </div>
          ))}
        </div>
      </main>

      {scan && (
        <DieselFormDialog
          key={scan.loading ? "leyendo" : scan.result?.file_id} // Fix K (2026-10-01): se vuelve a montar cuando llega la lectura de la IA; antes los useState quedaban vacíos
          scan={scan}
          onClose={() => setScan(null)}
          onSaved={async () => { setScan(null); await cargar(); }}
        />
      )}
      {camaraAbierta && (
        <CamaraDocumento titulo={t("Align the receipt inside the frame")} onCapture={onFotoCapturada} onCancel={() => setCamaraAbierta(false)} />
      )}
    </div>
  );
}

function DieselFormDialog({ scan, onClose, onSaved }) {
  const r = scan.result;
  const [fecha, setFecha] = useState(r?.fecha || "");
  const [monto, setMonto] = useState(r?.monto != null ? String(r.monto) : "");
  const [galones, setGalones] = useState(r?.galones != null ? String(r.galones) : "");
  const [precioGalon, setPrecioGalon] = useState(r?.precio_galon != null ? String(r.precio_galon) : "");
  const [estadoUs, setEstadoUs] = useState(r?.estado_us || "");
  const [estacion, setEstacion] = useState(r?.estacion || "");
  const [ciudad, setCiudad] = useState(r?.ciudad || "");
  const [saving, setSaving] = useState(false);

  const guardar = async (e) => {
    e.preventDefault();
    if (!fecha || !monto || !galones || !estadoUs) {
      toast.error(t("Date, amount, gallons and state are required"));
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.post("/chofer/diesel", {
        fecha, monto: parseFloat(monto), galones: parseFloat(galones),
        precio_galon: precioGalon ? parseFloat(precioGalon) : null,
        estado_us: estadoUs, estacion, ciudad,
        foto_file_id: r.file_id, original_file_id: r.original_file_id, extraccion_ia: r.extraccion_ia,
      });
      if (data.duplicado) toast.warning(t("This looks like a duplicate: a receipt with the same date and amount already exists"));
      toast.success(t("Receipt saved"));
      onSaved();
    } catch (err) { toast.error(apiError(err)); } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center p-0 sm:p-4">
      <div className="bg-white w-full sm:max-w-lg sm:rounded-xl max-h-[92vh] overflow-y-auto p-5">
        <h2 className="font-bold text-lg mb-4">{t("Fuel receipt")}</h2>
        {scan.loading ? (
          <div className="py-10 flex flex-col items-center gap-3 text-ink-2" data-testid="diesel-scan-revisando">
            <Loader2 className="h-8 w-8 animate-spin text-ink" />
            <p className="font-medium">{t("Reading the ticket…")}</p>
          </div>
        ) : (
          <div className="space-y-4">
            <img src={r.preview} alt={t("ticket")} className="w-full rounded-lg border max-h-56 object-contain bg-surface-2" data-testid="diesel-scan-preview" />
            {/* Fix S (2026-10-14): si el servidor no encontró los bordes del ticket deja la foto entera; se le avisa al chofer para que la repita. */}
            {r.encontro_contorno === false && (
              <p data-testid="diesel-scan-sin-bordes" className="text-xs text-[#7A5A10] bg-[#FBF5E6] border border-[#E9D9B0] rounded-lg p-2">{t("We could not find the edges of the ticket, so the whole photo was kept. For a cleaner scan, lay the ticket flat on a dark surface so it fills the frame, and take it again.")}</p>
            )}
            {!r.extraccion_ia && <p className="text-xs text-[#B07A12] bg-[#FBF5E6] border border-[#E9D9B0] rounded-lg p-2">{t("The ticket could not be read automatically. Fill in the details by hand.")}</p>}
            <form onSubmit={guardar} className="space-y-3">
              <div><Label className="eyebrow">{t("Date *")}</Label><Input data-testid="diesel-form-fecha" type="date" value={fecha} onChange={(e) => setFecha(e.target.value)} className="mt-1 h-12" required /></div>
              <div className="grid grid-cols-2 gap-2">
                <div><Label className="eyebrow">{t("Amount *")}</Label><Input data-testid="diesel-form-monto" type="number" step="0.01" value={monto} onChange={(e) => setMonto(e.target.value)} className="mt-1 h-12" required /></div>
                <div><Label className="eyebrow">{t("Gallons *")}</Label><Input data-testid="diesel-form-galones" type="number" step="0.001" value={galones} onChange={(e) => setGalones(e.target.value)} className="mt-1 h-12" required /></div>
              </div>
              <div><Label className="eyebrow">{t("Price per gallon")}</Label><Input data-testid="diesel-form-precio" type="number" step="0.01" value={precioGalon} onChange={(e) => setPrecioGalon(e.target.value)} className="mt-1 h-12" /></div>
              <div>
                <Label className="eyebrow">{t("State *")}</Label>
                <Select value={estadoUs} onValueChange={setEstadoUs}>
                  <SelectTrigger data-testid="diesel-form-estado" className="h-12 bg-white mt-1"><SelectValue placeholder={t("Choose state")} /></SelectTrigger>
                  <SelectContent className="bg-white max-h-64">
                    {ESTADOS_US.map((e) => <SelectItem key={e} value={e}>{e}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div><Label className="eyebrow">{t("Station")}</Label><Input data-testid="diesel-form-estacion" value={estacion} onChange={(e) => setEstacion(e.target.value)} className="mt-1 h-12" /></div>
              <div><Label className="eyebrow">{t("City")}</Label><Input data-testid="diesel-form-ciudad" value={ciudad} onChange={(e) => setCiudad(e.target.value)} className="mt-1 h-12" /></div>
              <div className="flex gap-2 pt-2">
                <Button type="button" variant="outline" className="h-12 flex-1" onClick={onClose}>{t("Cancel")}</Button>
                <Button type="submit" data-testid="diesel-form-guardar" className="h-12 flex-1 bg-brand hover:bg-brand-dark text-white" disabled={saving}>{saving ? <Loader2 className="h-5 w-5 animate-spin" /> : t("Save")}</Button>
              </div>
            </form>
          </div>
        )}
      </div>
    </div>
  );
}
